import { useNavigate } from 'react-router-dom';
import { Upload, Plus, FileText, ArrowRight, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useFeatureGate } from '@/hooks/useFeatureGate';

interface ProjectSetupChoiceProps {
  onUploadClick?: () => void;
  onManualClick?: () => void;
}

export function ProjectSetupChoice({ onUploadClick, onManualClick }: ProjectSetupChoiceProps) {
  const navigate = useNavigate();
  const { canUseAI } = useFeatureGate();

  const handleUploadClick = () => {
    // AI upload requires Pro subscription
    if (!canUseAI) return;

    if (onUploadClick) {
      onUploadClick();
    } else {
      navigate('/projects/create');
    }
  };

  const handleManualClick = () => {
    if (onManualClick) {
      onManualClick();
    } else {
      navigate('/projects/create');
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Option 1: Upload Documents */}
      <Card className={`border-2 transition-all ${
        canUseAI
          ? 'hover:border-ingesta-blue-500 hover:shadow-lg cursor-pointer group'
          : 'opacity-75 cursor-not-allowed'
      }`}>
        <CardContent className="p-8 flex flex-col h-full">
          <div className={`rounded-full w-16 h-16 flex items-center justify-center transition-colors ${
            canUseAI
              ? 'bg-ingesta-blue-100 group-hover:bg-ingesta-blue-200'
              : 'bg-gray-100'
          }`}>
            {canUseAI ? (
              <Upload className="h-8 w-8 text-ingesta-blue-600" />
            ) : (
              <Lock className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <div className="flex-1 mt-4">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold">Upload Documents</h3>
              {!canUseAI && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                  Pro
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Upload project documents and we'll automatically extract the project name,
              goals, and key information using AI.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <FileText className={`h-4 w-4 mr-2 mt-0.5 ${canUseAI ? 'text-ingesta-blue-500' : 'text-gray-400'}`} />
                <span>Automatically detect project details</span>
              </li>
              <li className="flex items-start">
                <FileText className={`h-4 w-4 mr-2 mt-0.5 ${canUseAI ? 'text-ingesta-blue-500' : 'text-gray-400'}`} />
                <span>Extract milestones and goals</span>
              </li>
              <li className="flex items-start">
                <FileText className={`h-4 w-4 mr-2 mt-0.5 ${canUseAI ? 'text-ingesta-blue-500' : 'text-gray-400'}`} />
                <span>Get AI-powered insights</span>
              </li>
            </ul>
          </div>
          <Button
            className={`w-full mt-6 ${canUseAI ? 'group-hover:shadow-md' : ''}`}
            onClick={handleUploadClick}
            disabled={!canUseAI}
          >
            {canUseAI ? (
              <>
                Upload & Create
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Upgrade to Pro
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Option 2: Create Manually */}
      <Card className="border-2 hover:border-ingesta-blue-500 hover:shadow-lg transition-all cursor-pointer group">
        <CardContent className="p-8 flex flex-col h-full">
          <div className="rounded-full bg-ingesta-teal-100 w-16 h-16 flex items-center justify-center group-hover:bg-ingesta-teal-200 transition-colors">
            <Plus className="h-8 w-8 text-ingesta-teal-600" />
          </div>
          <div className="flex-1 mt-4">
            <h3 className="text-xl font-bold mb-2">Create Manually</h3>
            <p className="text-gray-600 text-sm mb-4">
              Start with a blank project and add all the details yourself.
              Perfect if you're starting fresh.
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <FileText className="h-4 w-4 mr-2 mt-0.5 text-ingesta-teal-500" />
                <span>Enter project name and description</span>
              </li>
              <li className="flex items-start">
                <FileText className="h-4 w-4 mr-2 mt-0.5 text-ingesta-teal-500" />
                <span>Add milestones and KPIs</span>
              </li>
              <li className="flex items-start">
                <FileText className="h-4 w-4 mr-2 mt-0.5 text-ingesta-teal-500" />
                <span>Full control over setup</span>
              </li>
            </ul>
          </div>
          <Button
            variant="outline"
            className="w-full group-hover:shadow-md mt-6"
            onClick={handleManualClick}
          >
            Create Project
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
